/**
 * Procedural texture atlas. Every block face the mesher can emit lives on one
 * 256x256 canvas painted at boot: zero image assets means zero network fetches
 * and zero decode jank, and the look is identical on every machine because the
 * per-pixel variation comes from the sim's deterministic `hash3`, not from
 * `Math.random`.
 *
 * Layout: `ATLAS_TILE_COUNT` x `ATLAS_TILE_COUNT` grid of 32px tiles, addressed
 * by the `{ u, v }` tile coords in `src/data/blocks.ts` (u = column, v = row,
 * both from the canvas top-left). The registry today uses u <= 7 and v <= 2,
 * so 8x8 leaves spare rows for M1 blocks without a re-layout.
 *
 * UV convention: `flipY` is OFF, so UV v = 0 samples the canvas top row. The
 * mesher maps a tile's painted top edge to `tile.v / ATLAS_TILE_COUNT`.
 */

import * as THREE from "three";
import { hash3 } from "../sim/noise";

/** Tiles per atlas row and column. Square keeps the canvas a power of two. */
export const ATLAS_TILE_COUNT = 8;

const TILE_PX = 32;

export interface Atlas {
  readonly texture: THREE.CanvasTexture;
  dispose(): void;
}

type Rgb = [number, number, number];
/** Maps one tile-local pixel (0..31, y down from the painted top) to a colour. */
type Painter = (x: number, y: number) => Rgb;

/** Base colour plus a per-pixel brightness wobble of +/- amount. */
function jitter(base: Rgb, amount: number, salt: number, x: number, y: number): Rgb {
  const j = (hash3(salt, x, y, 0) - 0.5) * 2 * amount;
  return [base[0] + j, base[1] + j, base[2] + j];
}

const grassTop: Painter = (x, y) =>
  hash3(11, x, y, 0) < 0.07 ? [66, 118, 46] : jitter([90, 152, 62], 15, 11, x, y);

const dirt: Painter = (x, y) =>
  hash3(14, x, y, 0) < 0.06 ? [96, 66, 44] : jitter([122, 86, 58], 14, 14, x, y);

const grassSide: Painter = (x, y) => {
  // Ragged grass lip over a dirt body; the lip height varies per column.
  const edge = 4 + Math.floor(hash3(12, x, 0, 1) * 4);
  return y < edge ? jitter([90, 152, 62], 15, 13, x, y) : dirt(x, y);
};

const stone: Painter = (x, y) =>
  hash3(15, x, y, 0) < 0.08 ? [98, 98, 102] : jitter([128, 128, 132], 9, 15, x, y);

const cobble: Painter = (x, y) => {
  if (x % 8 === 0 || y % 8 === 0) return jitter([66, 66, 70], 6, 16, x, y);
  const shade = 0.85 + hash3(17, x >> 3, y >> 3, 2) * 0.3;
  const [r, g, b] = jitter([128, 128, 132], 8, 17, x, y);
  return [r * shade, g * shade, b * shade];
};

const logSide: Painter = (x, y) => {
  const stripe = 0.75 + hash3(18, x >> 1, 0, 3) * 0.5;
  const [r, g, b] = jitter([104, 78, 50], 8, 18, x, y);
  return [r * stripe, g * stripe, b * stripe];
};

const logTop: Painter = (x, y) => {
  // Square end-grain rings around the tile centre, bark at the rim.
  const d = Math.max(Math.abs(x - 15.5), Math.abs(y - 15.5));
  if (d > 13.5) return jitter([96, 70, 44], 8, 19, x, y);
  return Math.floor(d) % 2 === 0 ? jitter([170, 134, 90], 8, 19, x, y) : jitter([132, 100, 64], 8, 19, x, y);
};

const leaves: Painter = (x, y) =>
  // The "holes" are painted near-black, not transparent: the opaque material
  // ignores texture alpha and a real hole would need alphaTest on everything.
  hash3(20, x, y, 0) < 0.16 ? [14, 30, 14] : jitter([48, 98, 40], 18, 20, x, y);

const plank: Painter = (x, y) => {
  const row = y >> 3;
  if (y % 8 === 0 || x % 16 === (row % 2 === 0 ? 15 : 7)) return [102, 78, 48];
  const shade = 0.85 + hash3(21, 0, row, 4) * 0.3;
  const [r, g, b] = jitter([176, 140, 92], 8, 21, x, y);
  return [r * shade, g * shade, b * shade];
};

const sand: Painter = (x, y) => jitter([219, 205, 158], 11, 22, x, y);

const gravel: Painter = (x, y) => {
  const pick = hash3(23, x >> 1, y >> 1, 5);
  const base: Rgb =
    pick < 0.3 ? [152, 146, 140] : pick < 0.55 ? [122, 116, 110] : pick < 0.8 ? [100, 94, 90] : [136, 124, 112];
  return jitter(base, 8, 23, x, y);
};

const bedrock: Painter = (x, y) => {
  const cell = hash3(24, x >> 2, y >> 2, 6);
  return jitter(cell < 0.5 ? [44, 44, 50] : [96, 96, 102], 8, 24, x, y);
};

const deepstone: Painter = (x, y) =>
  hash3(33, x, y, 0) < 0.06 ? [76, 76, 88] : jitter([56, 56, 66], 7, 33, x, y);

/** Stone-family base with 4x4 blocky ore blobs rolled on a coarser grid. */
function ore(salt: number, blob: Rgb, chance: number, basePaint: Painter): Painter {
  return (x, y) =>
    hash3(salt, x >> 2, y >> 2, 7) < chance ? jitter(blob, 14, salt, x, y) : basePaint(x, y);
}

const coalOre = ore(25, [40, 40, 44], 0.22, stone);
const ironOre = ore(26, [206, 160, 112], 0.18, stone);
const deepIronOre = ore(27, [222, 146, 82], 0.2, deepstone);

const torch: Painter = (x, y) => {
  // Unused by the mesher in M0 (torches emit no faces) but painted so the
  // atlas stays complete for M1.
  if (x >= 13 && x <= 18) {
    return y < 9 ? jitter([255, 224, 130], 20, 28, x, y) : jitter([112, 82, 46], 10, 28, x, y);
  }
  return [26, 22, 18];
};

const lamp: Painter = (x, y) => {
  const d = Math.hypot(x - 15.5, y - 15.5);
  return d < 9 ? jitter([255, 232, 152], 14, 29, x, y) : jitter([198, 158, 92], 12, 29, x, y);
};

const water: Painter = (x, y) =>
  hash3(30, x, y >> 1, 8) < 0.09 ? [112, 162, 226] : jitter([62, 118, 205], 10, 30, x, y);

const limestone: Painter = (x, y) =>
  hash3(31, x, y, 0) < 0.06 ? [138, 134, 118] : jitter([172, 168, 150], 9, 31, x, y);

const shale: Painter = (x, y) => {
  const band = (y >> 2) % 2 === 0 ? 1 : 0.88;
  const [r, g, b] = jitter([98, 100, 108], 8, 32, x, y);
  return [r * band, g * band, b * band];
};

/** Tile painters keyed by the registry's `{ u, v }` coords. */
const PAINTERS: ReadonlyArray<{ u: number; v: number; paint: Painter }> = [
  { u: 0, v: 0, paint: grassTop },
  { u: 1, v: 0, paint: grassSide },
  { u: 2, v: 0, paint: dirt },
  { u: 3, v: 0, paint: stone },
  { u: 4, v: 0, paint: cobble },
  { u: 5, v: 0, paint: logSide },
  { u: 6, v: 0, paint: logTop },
  { u: 7, v: 0, paint: leaves },
  { u: 0, v: 1, paint: plank },
  { u: 1, v: 1, paint: sand },
  { u: 2, v: 1, paint: gravel },
  { u: 3, v: 1, paint: bedrock },
  { u: 4, v: 1, paint: coalOre },
  { u: 5, v: 1, paint: ironOre },
  { u: 6, v: 1, paint: deepIronOre },
  { u: 7, v: 1, paint: torch },
  { u: 0, v: 2, paint: lamp },
  { u: 1, v: 2, paint: water },
  { u: 2, v: 2, paint: limestone },
  { u: 3, v: 2, paint: shale },
  { u: 4, v: 2, paint: deepstone },
];

export function createAtlasTexture(): Atlas {
  const canvas = document.createElement("canvas");
  canvas.width = ATLAS_TILE_COUNT * TILE_PX;
  canvas.height = ATLAS_TILE_COUNT * TILE_PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("atlas: 2D context unavailable");

  for (const { u, v, paint } of PAINTERS) {
    const image = ctx.createImageData(TILE_PX, TILE_PX);
    for (let y = 0; y < TILE_PX; y++) {
      for (let x = 0; x < TILE_PX; x++) {
        const [r, g, b] = paint(x, y);
        const i = (x + y * TILE_PX) * 4;
        image.data[i] = r;
        image.data[i + 1] = g;
        image.data[i + 2] = b;
        image.data[i + 3] = 255;
      }
    }
    ctx.putImageData(image, u * TILE_PX, v * TILE_PX);
  }

  const texture = new THREE.CanvasTexture(canvas);
  // Chunky nearest-neighbour texels are the voxel look; mipmaps would smear
  // the painted noise into grey soup at distance.
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  // UV v = 0 is the canvas top row; the mesher builds UVs to match.
  texture.flipY = false;
  texture.colorSpace = THREE.SRGBColorSpace;

  return {
    texture,
    dispose(): void {
      texture.dispose();
    },
  };
}
